import { useEffect } from 'react';
import type { PortfolioItemWithUrl } from '../../../hooks/usePortfolioStore';

interface Props {
  items: PortfolioItemWithUrl[];
  currentIndex: number;
  onClose: () => void;
  onChange: (index: number) => void;
}

export default function Lightbox({ items, currentIndex, onClose, onChange }: Props) {
  const item = items[currentIndex];

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && currentIndex > 0) onChange(currentIndex - 1);
      if (e.key === 'ArrowRight' && currentIndex < items.length - 1) onChange(currentIndex + 1);
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [currentIndex, items.length, onClose, onChange]);

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close */}
      <button
        className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center text-white/60 hover:text-white cursor-pointer transition-colors z-10"
        onClick={onClose}
      >
        <i className="ri-close-line text-2xl" />
      </button>

      {/* Counter */}
      <div
        className="absolute top-5 left-5 text-white/40 text-sm z-10 font-light"
        style={{ fontFamily: "'Montserrat', sans-serif" }}
      >
        {String(currentIndex + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}
      </div>

      {/* Left Arrow */}
      {currentIndex > 0 && (
        <button
          className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white cursor-pointer transition-colors z-10"
          onClick={(e) => { e.stopPropagation(); onChange(currentIndex - 1); }}
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
      )}

      {/* Image */}
      <div
        className="flex items-center justify-center mx-16 md:mx-24 max-w-5xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={item.imageUrl}
          alt=""
          className="max-h-[85vh] max-w-full object-contain"
        />
      </div>

      {/* Right Arrow */}
      {currentIndex < items.length - 1 && (
        <button
          className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white cursor-pointer transition-colors z-10"
          onClick={(e) => { e.stopPropagation(); onChange(currentIndex + 1); }}
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      )}

      {/* Dot Nav */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
        {items.slice(0, 15).map((_, idx) => (
          <button
            key={idx}
            onClick={(e) => { e.stopPropagation(); onChange(idx); }}
            className={`rounded-full cursor-pointer transition-all duration-200 ${
              idx === currentIndex
                ? 'w-5 h-1.5 bg-white'
                : 'w-1.5 h-1.5 bg-white/25 hover:bg-white/50'
            }`}
          />
        ))}
        {items.length > 15 && (
          <span className="text-white/30 text-xs ml-1 self-center">+{items.length - 15}</span>
        )}
      </div>
    </div>
  );
}
